import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { FloatingPlus } from "./Decor";
import HeroShader from "./HeroShader";

const word = (text, delay = 0) => (
  <span className="reveal-mask align-bottom">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1], delay }}
      className="inline-block"
    >
      {text}
    </motion.span>
  </span>
);

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full pt-32 sm:pt-40 pb-20 overflow-hidden"
    >
      {/* Domain-warped fBM smoke shader */}
      <HeroShader />
      {/* Soft dark gradient at the bottom for text legibility */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, rgba(5,5,5,0.85) 0%, rgba(5,5,5,0) 100%)",
        }}
      />

      {/* Decorative floating plus marks */}
      <FloatingPlus x="92%" y="22%" size={22} delay={0.6} accent />
      <FloatingPlus x="6%" y="68%" size={18} delay={0.9} />
      <FloatingPlus x="78%" y="78%" size={14} delay={1.2} />

      {/* Vertical side label */}
      <div
        aria-hidden="true"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 origin-center -rotate-90 font-mono text-[10px] uppercase tracking-[0.4em] text-zinc-600 whitespace-nowrap"
      >
        ML — 2026 / Studio digital indépendant
      </div>

      {/* Top meta row */}
      <div className="relative z-10 px-5 sm:px-8 lg:px-12 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-16 sm:mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.25em] text-zinc-500 max-w-xs"
          data-testid="hero-meta"
        >
          <span className="text-[var(--accent)]">●</span> Disponible — Nouveaux projets
          <br />
          Studio digital indépendant • Estd. 2024
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500"
        >
          (01) — Hero
        </motion.div>
      </div>

      {/* Massive headline */}
      <h1
        data-testid="hero-heading"
        className="relative z-10 font-display uppercase px-5 sm:px-8 lg:px-12 leading-[0.86] tracking-[-0.04em] text-[15vw] sm:text-[14vw] lg:text-[12.5vw]"
      >
        <span className="block">{word("Votre site,", 0.15)}</span>
        <span className="block">
          {word("une ", 0.3)}
          <span className="text-outline">{word("signature", 0.42)}</span>
        </span>
        <span className="block">
          {word("digitale.", 0.55)}
        </span>
      </h1>

      {/* Bottom row */}
      <div className="relative z-10 px-5 sm:px-8 lg:px-12 mt-16 sm:mt-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="md:col-span-5 lg:col-span-4 md:col-start-7 lg:col-start-8 font-body text-base sm:text-lg leading-relaxed text-zinc-400"
          data-testid="hero-paragraph"
        >
          Nous concevons des sites rapides, optimisés pour Google et pensés pour la conversion — afin d'aider artisans, indépendants et PME à transformer leurs visiteurs en clients.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="md:col-span-12 flex flex-wrap items-center gap-4 mt-6"
        >
          <Link
            to="/mockup-gratuit"
            data-testid="hero-cta-primary"
            className="btn-pill btn-pill-accent"
          >
            Voir ce qu'on ferait pour mon commerce
            <ArrowUpRight className="w-4 h-4" />
          </Link>
          <Link
            to="/portfolio"
            data-testid="hero-cta-secondary"
            className="btn-pill"
          >
            Voir nos réalisations
          </Link>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 ml-2">
            Aperçu offert • Sans engagement
          </span>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-8 hidden md:flex flex-col items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500"
        data-testid="hero-scroll-indicator"
      >
        <span>Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
        >
          <ArrowDown className="w-4 h-4" />
        </motion.div>
      </motion.div>
    </section>
  );
}
